import { afterRotate, getPointsAfterRotate } from '@src/slide/infrastructure/module/independent/graphic/index';
import _has from 'lodash/has';

interface Rect {
    left: number,
    right: number,
    top: number,
    bottom: number,
}

interface Point {
    x: number,
    y: number,
    rect?: Rect,
    direction?: number | undefined,
    rotate?: number,
    noConnectedModel?: boolean,
}

const minGap = 30;
const addLengthByDirection = {
    0: {
        x: 0,
        y: -minGap,
    },
    1: {
        x: minGap,
        y: 0,
    },
    2: {
        x: 0,
        y: minGap,
    },
    3: {
        x: -minGap,
        y: 0,
    },
};

export function getFourPoints(pointInfo: Point) {
    if (pointInfo.rect) {
        if (_has(pointInfo, 'rotate')) {
            const { rotate } = pointInfo;
            return getPointsAfterRotate({
                left: pointInfo.rect.left,
                top: pointInfo.rect.top,
                width: pointInfo.rect.right - pointInfo.rect.left,
                height: pointInfo.rect.bottom - pointInfo.rect.top,
            }, rotate);
        }
        return [{
            x: pointInfo.rect.left,
            y: pointInfo.rect.top,
        }, {
            x: pointInfo.rect.right,
            y: pointInfo.rect.top,
        }, {
            x: pointInfo.rect.left,
            y: pointInfo.rect.bottom,
        }, {
            x: pointInfo.rect?.right,
            y: pointInfo.rect?.bottom,
        }];
    }
    return [];
}

/**
 * 判断目标点是否在线段上
 * @param segmentPoint1 内联点，可能含有有noConnectedModel属性，也是线段的一端
 * @param segmentPoint2 中点，是线段的另一端
 * @param targetPoint 目标点
 */
export function isTargetInLineSegment(segmentPoint1: Point, segmentPoint2: Point, targetPoint: Point) {
    if (segmentPoint1.noConnectedModel) {
        return false;
    }
    const vector1 = {
        x: segmentPoint1.x - segmentPoint2.x,
        y: segmentPoint1.y - segmentPoint2.y,
    };
    const vector2 = {
        x: segmentPoint1.x - targetPoint.x,
        y: segmentPoint1.y - targetPoint.y,
    };
    const minX = Math.min(segmentPoint1.x, segmentPoint2.x);
    const maxX = Math.max(segmentPoint1.x, segmentPoint2.x);
    const minY = Math.min(segmentPoint1.y, segmentPoint2.y);
    const maxY = Math.max(segmentPoint1.y, segmentPoint2.y);
    const isTargetInLine = vector1.x * vector2.y === vector1.y * vector2.x;
    return isTargetInLine
        && targetPoint.x >= minX
        && targetPoint.x <= maxX
        && targetPoint.y >= minY
        && targetPoint.y <= maxY;
}

// 判断路径是否经过起点或终点
export function isPathThroughStartOrEnd(path: Point[]) {
    const [start, startInnerPoint, middlePoint, endInnerPoint, end] = path;
    return isTargetInLineSegment(startInnerPoint, middlePoint, start)
        || isTargetInLineSegment(endInnerPoint, middlePoint, end)
        || isTargetInLineSegment(startInnerPoint, middlePoint, end)
        || isTargetInLineSegment(endInnerPoint, middlePoint, start);
}

// 计算当前路径点集的折线数量
export function getPathTurnNum(path: Point[]) {
    let num = 1;
    // 如果有连接的图形，则给定初始的检查方向，如果cur的y与下一个y相等，则继续沿y方向寻找，如果不等，则换成x方向继续向后寻找
    if (!path[1].noConnectedModel) {
        let y = false;
        if (path[0].y === path[1].y) {
            y = true;
        }
        path.forEach((currentValue, index) => {
            if (index === path.length - 1) {
                return;
            }
            if (y) {
                if (!(currentValue.y === path[(index + 1)].y)) {
                    num = num + 1;
                    y = !y;
                }
            } else {
                if (!(currentValue.x === path[(index + 1)].x)) {
                    num = num + 1;
                    y = !y;
                }
            }
        });
    } else {
        // 如果没有所连接的图形，则第一个点与第二个点的xy均相等，需要按照两个方向都计算一次，最后取小的数字
        let num1 = 1;
        let num2 = 1;
        let y = false;
        path.forEach((currentValue, index) => {
            if (index === path.length - 1) {
                return;
            }
            if (y) {
                if (!(currentValue.y === path[(index + 1)].y)) {
                    num1 = num1 + 1;
                    y = !y;
                }
            } else {
                if (!(currentValue.x === path[(index + 1)].x)) {
                    num1 = num1 + 1;
                    y = !y;
                }
            }
        });
        y = true;
        path.forEach((currentValue, index) => {
            if (index === path.length - 1) {
                return;
            }
            if (y) {
                if (!(currentValue.y === path[(index + 1)].y)) {
                    num2 = num2 + 1;
                    y = !y;
                }
            } else {
                if (!(currentValue.x === path[(index + 1)].x)) {
                    num2 = num2 + 1;
                    y = !y;
                }
            }
        });
        num = num1 < num2 ? num1 : num2;
    }
    return num;
}
// 生成起点与终点附近的两个内联点
export function generateInnerPoint(pointInfo: Point) {
    const cornerPoints = getFourPoints(pointInfo);
    const rotate = pointInfo.rotate || 0;
    // 该点没有connectedModel的情况
    if (!cornerPoints.length) {
        return {
            x: pointInfo.x,
            y: pointInfo.y,
            noConnectedModel: true,
        };
    }
    let product;
    // 针对一些端点可能在该图形的外接矩形内部的端点，规定了其出发方向，如果存在规定的出发方向，则用这个，不存在才进行计算
    if (pointInfo.direction !== 0 && !pointInfo.direction) {
        // 获取该点在形状的哪条边上，以判断初始、结束的出发方向
        product = cornerPoints.map((currentValue, index, arr) => {
            const edgeVector = {
                x: currentValue.x - arr[(index + 1) % 4].x,
                y: currentValue.y - arr[(index + 1) % 4].y,
            };
            const pointVector = {
                x: pointInfo.x - currentValue.x,
                y: pointInfo.y - currentValue.y,
            };
            return Math.abs((edgeVector.x * pointVector.y) - (edgeVector.y * pointVector.x));
        });

        // 此处利用向量平行的方式判断目标点在哪条边上，本应向量乘积为0时为平行情况
        // 但由于旋转的存在导致浮点数的出现，计算出的结果并不精确为0，转而取四个方向上的绝对值的最小值
        const addType = Math.floor((rotate + 45) / 90);
        const closeEdge = product.indexOf(Math.min(...product));
        const direction = (closeEdge + addType) % 4;
        return {
            x: pointInfo.x + addLengthByDirection[direction].x,
            y: pointInfo.y + addLengthByDirection[direction].y,
            direction,
        };
    }
    const addType = Math.floor((rotate + 45) / 90);
    const direction = (pointInfo.direction + addType) % 4;
    const innerPoint = { x: pointInfo.x, y: pointInfo.y, direction };
    cornerPoints.sort((a, b) => a.x - b.x);
    const minX = cornerPoints[0].x;
    const maxX = cornerPoints[3].x;
    cornerPoints.sort((a, b) => a.y - b.y);
    const minY = cornerPoints[0].y;
    const maxY = cornerPoints[3].y;
    switch (direction) {
        case 0:
            innerPoint.y = minY - minGap;
            break;
        case 1:
            innerPoint.x = maxX + minGap;
            break;
        case 2:
            innerPoint.y = maxY + minGap;
            break;
        case 3:
            innerPoint.x = minX - minGap;
            break;
        default:
            break;
    }
    return innerPoint;
}

// 根据起点与终点的形状生成中点位置
export function generateMiddlePosition(start, end, direction: number) {
    if ((direction % 2) === 1) {
        // 起点与终点的出发方向水平的情况
        if (start.rect?.bottom < end.rect?.top) {
            // 此时start的整体在end的上方
            return (start.rect?.bottom + end.rect?.top) / 2;
        }
        if (end.rect?.bottom < start.rect?.top) {
            // 此时end的整体在start的上方
            return (end.rect?.bottom + start.rect?.top) / 2;
        }
        // 此时start与end垂直方向上有重合的部分,需要从上方或者下方绕路
        return false;
    }
    // 起点与终点的出发方向垂直的情况
    if (start.rect?.right < end.rect?.left) {
        // 此时start的整体在end的左侧
        return (start.rect?.right + end.rect?.left) / 2;
    }
    if (end.rect?.right < start.rect?.left) {
        // 此时end的整体在start的左侧
        return (end.rect?.right + start.rect?.left) / 2;
    }
    // 此时start与end水平方向上有重合的部分,需要从左侧或者右侧绕路
    return false;
}

// 单纯解圈复杂度，没有实际意义
export function compareThreeCondition(value1: number, value2: number, value3: number): boolean {
    if ((value1 > value2 && value3 < value1 && value3 > value2)) {
        return true;
    }
    return false;
}

// 单纯解圈复杂度，没有实际意义
export function getMaxEdge(start, end, throughShape, direction): number {
    if (throughShape) {
        return throughShape.rect[direction];
    }
    return Math.max(start.rect?.[direction], end.rect?.[direction])
    || start.rect?.[direction]
    || end.rect?.[direction];
}

export function fixAPathToMiddle(
    start,
    end,
    newStartInnerPoint,
    newEndInnerPoint,
    extraPoint1,
    extraPoint2,
    fixX,
) {
    if (fixX) {
        if (compareThreeCondition(start.rect.left, end.rect.right, newStartInnerPoint.x)) {
            // 此时start整体在end的右侧，且newStartInnerPoint的x在start与end中间
            const newX = (start.rect.left + end.rect.right) / 2;
            newStartInnerPoint.x = newX;
            extraPoint1.x = newX;
        } else if (compareThreeCondition(start.rect.left, end.rect.right, newEndInnerPoint.x)) {
            // 此时start整体在end的右侧，且newEndInnerPoint的x在start与end中间
            const newX = (end.rect.right + start.rect.left) / 2;
            newEndInnerPoint.x = newX;
            extraPoint2.x = newX;
        } else if (compareThreeCondition(end.rect.left, start.rect.right, newEndInnerPoint.x)) {
            // 此时end整体在start的右侧，且newEndInnerPoint的x在start与end中间
            const newX = (start.rect.right + end.rect.left) / 2;
            newEndInnerPoint.x = newX;
            extraPoint2.x = newX;
        } else if (compareThreeCondition(end.rect.left, start.rect.right, newStartInnerPoint.x)) {
            // 此时end整体在start的右侧，且newStartInnerPoint的x在start与end中间
            const newX = (start.rect.right + end.rect.left) / 2;
            newStartInnerPoint.x = newX;
            extraPoint1.x = newX;
        }
    } else {
        if (compareThreeCondition(start.rect.top, end.rect.bottom, newStartInnerPoint.y)) {
            // 此时start整体在end的下方，且newStartInnerPoint的y在start与end中间
            const newY = (start.rect.top + end.rect.bottom) / 2;
            newStartInnerPoint.y = newY;
            extraPoint1.y = newY;
        } else if (compareThreeCondition(start.rect.top, end.rect.bottom, newEndInnerPoint.y)) {
            // 此时start整体在end的下方，且newEndInnerPoint的y在start与end中间
            const newY = (start.rect.top + end.rect.bottom) / 2;
            newEndInnerPoint.y = newY;
            extraPoint2.y = newY;
        } else if (compareThreeCondition(end.rect.top, start.rect.bottom, newEndInnerPoint.y)) {
            // 此时start整体在end的上方，且newEndInnerPoint的y在start与end中间
            const newY = (start.rect.bottom + end.rect.top) / 2;
            newEndInnerPoint.y = newY;
            extraPoint2.y = newY;
        } else if (compareThreeCondition(end.rect.top, start.rect.bottom, newStartInnerPoint.y)) {
            // 此时start整体在end的上方，且newStartInnerPoint的y在start与end中间
            const newY = (start.rect.bottom + end.rect.top) / 2;
            newStartInnerPoint.y = newY;
            extraPoint1.y = newY;
        }
    }
}

// 生成额外的点以进行绕路
export function generateExtraPathPoint(startInnerPoint, endInnerPoint, start, end, throughShape?) {
    const maxBottom = getMaxEdge(start, end, throughShape, 'bottom');
    const maxRight = getMaxEdge(start, end, throughShape, 'right');
    const newStartInnerPoint = {
        x: startInnerPoint.x,
        y: startInnerPoint.y,
    };
    const newEndInnerPoint = {
        x: endInnerPoint.x,
        y: endInnerPoint.y,
    };
    if ((startInnerPoint.direction % 2) === 1) {
        // 起点与终点的出发方向水平的情况
        const middleY = generateMiddlePosition(start, end, startInnerPoint.direction);
        const extraPoint1 = {
            x: startInnerPoint.x,
            y: middleY,
        };
        const extraPoint2 = {
            x: endInnerPoint.x,
            y: middleY,
        };
        if (!middleY) {
            // 需要绕路的情况下，规定从下方绕，否则会出现来回跳动的情况
            extraPoint1.y = maxBottom + minGap;
            extraPoint2.y = maxBottom + minGap;
            fixAPathToMiddle(start, end, newStartInnerPoint, newEndInnerPoint, extraPoint1, extraPoint2, true);
        }
        return { newStartInnerPoint, extraPoint1, extraPoint2, newEndInnerPoint };
    }
    // 起点与终点的出发方向垂直的情况
    const middleX = generateMiddlePosition(start, end, startInnerPoint.direction);
    const extraPoint1 = {
        x: middleX,
        y: startInnerPoint.y,
    };
    const extraPoint2 = {
        x: middleX,
        y: endInnerPoint.y,
    };
    if (!middleX) {
        // 需要绕路的情况下，规定从右侧绕，否则会出现来回跳动的情况
        extraPoint1.x = maxRight + minGap;
        extraPoint2.x = maxRight + minGap;
        fixAPathToMiddle(start, end, newStartInnerPoint, newEndInnerPoint, extraPoint1, extraPoint2, false);
    }
    return { newStartInnerPoint, extraPoint1, extraPoint2, newEndInnerPoint };
}

// 判断路径是否经过起点或终点所关联的形状
export function isPathThroughShape(points: Point[]) {
    const start = points[0];
    const end = points[points.length - 1];
    const startFourPoint = getFourPoints(start);
    const endFourPoint = getFourPoints(end);
    let throughStart = false;
    let throughEnd = false;
    let isThrough = false;
    points.forEach((item, index) => {
        if (index === points.length - 1) {
            return;
        }
        // vector1为路径上的当前点与下一点连接的向量
        const vector1 = {
            x: item.x - points[index + 1].x,
            y: item.y - points[index + 1].y,
        };
        // 判断线是否经过形状，需要判断该形状的每条边是否与该线相交
        // 判断线AB与线CD是否相交，判断条件为
        // 向量AC叉乘向量AD与向量BC叉乘向量BD异号 && 向量CA叉乘向量CB与向量DA叉乘向量DB异号
        if (index !== 0) {
            startFourPoint?.forEach((cur, indexStart) => {
                const nextIndex = (indexStart + 1) % startFourPoint.length;
                // vector2为路径上的一点与当前边的一点所连接的向量
                const vector2 = {
                    x: cur.x - item.x,
                    y: cur.y - item.y,
                };
                // vector3为路径上的一点与当前边的另一点所连接的向量
                const vector3 = {
                    x: startFourPoint[nextIndex].x - item.x,
                    y: startFourPoint[nextIndex].y - item.y,
                };
                // vector4为当前边的两个点所连接的向量
                const vector4 = {
                    x: cur.x - startFourPoint[nextIndex].x,
                    y: cur.y - startFourPoint[nextIndex].y,
                };
                // vector5为当前边的一点与路径上的下一点所连接的向量
                const vector5 = {
                    x: cur.x - points[index + 1].x,
                    y: cur.y - points[index + 1].y,
                };
                const dir1 = ((vector1.x * vector2.y) - (vector1.y * vector2.x));
                const dir2 = ((vector1.x * vector3.y) - (vector1.y * vector3.x));
                const dir3 = ((vector4.x * vector2.y) - (vector4.y * vector2.x));
                const dir4 = ((vector4.x * vector5.y) - (vector4.y * vector5.x));
                // 如果以下两向量叉乘均异号，则说明当前两条线段相交
                if ((dir1 * dir2 < 0) && (dir3 * dir4 < 0)) {
                    throughStart = true;
                    isThrough = true;
                }
            });
        }
        if (index !== points.length - 2) {
            endFourPoint?.forEach((cur, indexEnd) => {
                const nextIndex = (indexEnd + 1) % endFourPoint.length;
                // vectors的定义同start
                const vector2 = {
                    x: cur.x - item.x,
                    y: cur.y - item.y,
                };
                const vector3 = {
                    x: endFourPoint[nextIndex].x - item.x,
                    y: endFourPoint[nextIndex].y - item.y,
                };
                const vector4 = {
                    x: cur.x - endFourPoint[nextIndex].x,
                    y: cur.y - endFourPoint[nextIndex].y,
                };
                const vector5 = {
                    x: cur.x - points[index + 1].x,
                    y: cur.y - points[index + 1].y,
                };
                const dir1 = ((vector1.x * vector2.y) - (vector1.y * vector2.x));
                const dir2 = ((vector1.x * vector3.y) - (vector1.y * vector3.x));
                const dir3 = ((vector4.x * vector2.y) - (vector4.y * vector2.x));
                const dir4 = ((vector4.x * vector5.y) - (vector4.y * vector5.x));
                // 如果以下两向量叉乘均异号，则说明当前两条线段相交
                if ((dir1 * dir2 < 0) && (dir3 * dir4 < 0)) {
                    throughEnd = true;
                    isThrough = true;
                }
            });
        }
    });
    return { throughStart, throughEnd, isThrough };
}

export function handleRealNumThree(points: Point[]) {
    let newPoints = [...points];
    const [start, startInnerPoint, middlePoint, endInnerPoint, end] = points;
    // 折两次的情况，需要判断删除起始内联点与终止内联点后该路径与两端图形的交叉情况，优先选择不交叉的，如两种情况都交叉，需生成新点对其避让
    const points1 = [...newPoints];
    const points2 = [...newPoints];
    points1.splice(1, 1);
    points2.splice(3, 1);
    const isPoint1Through = isPathThroughShape(points1).isThrough;
    const isPoint2Through = isPathThroughShape(points2).isThrough;
    if (isPoint1Through && isPoint2Through) {
        const { throughStart } = isPathThroughShape(points);
        const throughShape = throughStart ? points[0] : points[4];
        const {
            newStartInnerPoint,
            extraPoint1,
            extraPoint2,
            newEndInnerPoint,
        } = generateExtraPathPoint(startInnerPoint, endInnerPoint, start, end, throughShape);
        newPoints = [start, newStartInnerPoint, extraPoint1, extraPoint2, newEndInnerPoint, end];
    } else {
        // 两种方式都不交叉，删除连续三个在一条直线上的中点
        let continuousArr: number[] = [];
        let y = false;
        if (points[0].y === points[1].y) {
            y = true;
        }
        // 判断哪三个点在一条直线上
        points.forEach((currentValue, index) => {
            if (index === points.length - 1 || continuousArr.length === 2) {
                return;
            }
            if (y) {
                if (!(currentValue.y === points[(index + 1)].y)) {
                    y = !y;
                    continuousArr = [index];
                } else {
                    continuousArr.push(index);
                }
            } else {
                if (!(currentValue.x === points[(index + 1)].x)) {
                    y = !y;
                    continuousArr = [index];
                } else {
                    continuousArr.push(index);
                }
            }
        });
        // continuousArr为连续在一条直线上的几个点，continuousArr[0]为连续在一条直线上的第一个点
        // tmpArr为连续在一条直线上的三个点
        const tmpArr = [points[continuousArr[0]], points[continuousArr[0] + 1], points[continuousArr[0] + 2]];
        if (points[continuousArr[0]].x === points[continuousArr[0] + 1].x) {
            // 如果连续点是在x方向上连续，则将其按照y的大小排序，删除y大小居中的元素
            tmpArr.sort((a, b) => a.y - b.y);
            const delIndex = newPoints.indexOf(tmpArr[1]);
            if (points[delIndex - 1].x !== points[delIndex + 1].x) {
                // 由于删除的是排序后的居中元素，可能导致删除后的path不连续，如果出现这种情况，则对其坐标进行修正
                newPoints[delIndex - 1].y = newPoints[delIndex + 1].y;
            }
            newPoints.splice(delIndex, 1);
        } else {
            // 如果连续点是在y方向上连续，则将其按照x的大小排序，删除x大小居中的元素
            tmpArr.sort((a, b) => a.x - b.x);
            const delIndex = newPoints.indexOf(tmpArr[1]);
            if (points[delIndex - 1].y !== points[delIndex + 1].y) {
                // 由于删除的是排序后的居中元素，可能导致删除后的path不连续，如果出现这种情况，则对其坐标进行修正
                newPoints[delIndex - 1].x = newPoints[delIndex + 1].x;
            }
            newPoints.splice(delIndex, 1);
        }
    }
    return newPoints;
}

// 根据路径的弯折次数删除多余的点
export function deleteExtraPoints(points: Point[]) {
    let newPoints = points;
    const realNum = getPathTurnNum(newPoints);
    // 如果起点或终点没有所连接的图形，则先删除其附近的内联点，再根据其弯折的次数，判断是否需要删除另一端的内联点
    // 如果两端都没有所连接的图形，外部控制其不会走本文件的算法
    // 理论上将当realNum=1的时候可以直接删掉中间的三个点，只保留首尾两个点
    // 但奇怪的是，删除三个点后渲染出的结果有问题，反而只删除两个点符合预期，似乎外部对直线的情况有特殊处理
    if (!points[0].rect) {
        newPoints.splice(1, 1);
        if (realNum === 2 || realNum === 1) {
            newPoints.splice(2, 1);
        }
        return newPoints;
    }
    if (!points[points.length - 1].rect) {
        newPoints.splice(points.length - 2, 1);
        if (realNum === 2 || realNum === 1) {
            newPoints.splice(1, 1);
        }
        return newPoints;
    }
    if (realNum === 1 || realNum === 2) {
        // 只折一次的情况，直接删除起始内联点与终止内联点，保留中点即可
        newPoints.splice(1, 1);
        newPoints.splice(2, 1);
    } else if (realNum === 3) {
        newPoints = handleRealNumThree(points);
    }
    return newPoints;
}

// 生成增强避让的路径
export function generateAvoidPath(paths: Point[][], points: Point[]) {
    const pathsThroughArr = paths.map(path => isPathThroughShape(path).isThrough);
    const notThroughIndex = pathsThroughArr.indexOf(false);
    return notThroughIndex === -1 ? points : paths[notThroughIndex];
}

// 该方法的目的是获得恰好五个点，以便后续进行增强避让的计算，如果不足五个点，则需要对其进行补齐
export function getTempPoints(points: Point[]): Point[] {
    if (points.length === 5) {
        return points;
    }
    if (points.length === 4) {
        if (!points[0].rect) {
            // 如果起始点没有所连接的图形，则补齐起始点
            return [points[0], points[0], points[1], points[2], points[3]];
        }
        // 如果终点没有所连接的图形，则补齐终点
        return [points[0], points[1], points[2], points[3], points[3]];
    }
    // 如果只有三个点，则需要再次生成内联点以对其进行补齐
    const startInnerPoint = generateInnerPoint(points[0]);
    const endInnerPoint = generateInnerPoint(points[2]);
    return [points[0], startInnerPoint, points[1], endInnerPoint, points[2]];
}

export function isPointInShape(point: Point) {
    if (point.rect) {
        const { x, y } = point;
        const { top, right, bottom, left } = point.rect;
        if (x < right && x > left && y < bottom && y > top) {
            return true;
        }
    }
    return false;
}

// 单纯解圈复杂度，没有实际意义
export function getPathByCondition(condition, path1, path2) {
    return condition ? path1 : path2;
}

// 单纯解圈复杂度，没有实际意义
export function booleanAnd(condition1, condition2) {
    return condition1 && condition2;
}

// 单纯解圈复杂度，没有实际意义
export function booleanOr(condition1, condition2) {
    return condition1 || condition2;
}

// 对路径进行增强避让
export function enhancedAvoidance(points) {
    // 路径上只有两个点时为直线，无需进行避让，路径上有6个点时，避让过程已在生成额外路径时进行，这里不需要重复避让
    if (booleanOr(points.length < 3, points.length > 5)) {
        return points;
    }
    const { throughStart, throughEnd, isThrough } = isPathThroughShape(points);
    if (booleanAnd(points.length === 3, isThrough)) {
        return points;
    }
    const [start, startInnerPoint, middlePoint, endInnerPoint, end] = getTempPoints(points);
    if (booleanAnd(throughStart, throughEnd)) {
        // 如果起始两端均穿过所连接的图形，则起点与终点出发方向必然为一垂直一水平，需要将路径上的其中一条边平移至两个图形的同一侧
        const eightPoints = [...getFourPoints(start), ...getFourPoints(end)];
        eightPoints.sort((a, b) => a.x - b.x);
        const minX = eightPoints[0].x;
        const maxX = eightPoints[7].x;
        eightPoints.sort((a, b) => a.y - b.y);
        const minY = eightPoints[0].y;
        const maxY = eightPoints[7].y;
        if (startInnerPoint.x === middlePoint.x) {
            // 如果是x方向上穿过了图形，继续判断start的触发方向是向左或者向右，end的触发方向是向上还是向下
            const path1 = getPathByCondition(
                startInnerPoint.x - start.x < 0,
                [start,
                    { x: minX - minGap, y: startInnerPoint.y },
                    { x: minX - minGap, y: middlePoint.y },
                    endInnerPoint,
                    end],
                [start,
                    { x: maxX + minGap, y: startInnerPoint.y },
                    { x: maxX + minGap, y: middlePoint.y },
                    endInnerPoint,
                    end],
            );
            const path2 = getPathByCondition(
                endInnerPoint.y - end.y < 0,
                [start,
                    startInnerPoint,
                    { x: middlePoint.x, y: minY - minGap },
                    { x: endInnerPoint.x, y: minY - minGap },
                    end],
                [start,
                    startInnerPoint,
                    { x: middlePoint.x, y: maxY + minGap },
                    { x: endInnerPoint.x, y: maxY + minGap },
                    end],
            );
            return generateAvoidPath([path1, path2], points);
        }
        // 如果是y方向上穿过了图形，继续判断start的触发方向是向上或者向下，end的触发方向是向左还是向右
        const path1 = getPathByCondition(
            startInnerPoint.y - start.y < 0,
            [start,
                { x: startInnerPoint.x, y: minY - minGap },
                { x: middlePoint.x, y: minY - minGap },
                endInnerPoint,
                end],
            [start,
                { x: startInnerPoint.x, y: maxY + minGap },
                { x: middlePoint.x, y: maxY + minGap },
                endInnerPoint,
                end],
        );
        const path2 = getPathByCondition(
            endInnerPoint.x - end.x < 0,
            [start,
                startInnerPoint,
                { x: minX - minGap, y: middlePoint.y },
                { x: minX - minGap, y: endInnerPoint.y },
                end],
            [start,
                startInnerPoint,
                { x: maxX + minGap, y: middlePoint.y },
                { x: maxX + minGap, y: endInnerPoint.y },
                end],
        );
        return generateAvoidPath([path1, path2], points);
    }
    if (booleanAnd(!start.rotate, !end.rotate)) {
        // 如果存在旋转则不进行增强避让，否则会出现鬼畜
        // 仅穿过一端所连接的图形的话，只需要避让所穿过的图形
        if (throughStart) {
            const [leftTop, rightTop, rightBottom, leftBottom] = getFourPoints(start);
            if (start.x === startInnerPoint.x) {
                return getPathByCondition(
                    endInnerPoint.x < end.x,
                    [start,
                        startInnerPoint,
                        { x: leftTop.x - minGap, y: startInnerPoint.y },
                        { x: leftTop.x - minGap, y: endInnerPoint.y },
                        end],
                    [start, startInnerPoint,
                        { x: rightTop.x + minGap, y: startInnerPoint.y },
                        { x: rightTop.x + minGap, y: endInnerPoint.y },
                        end],
                );
            }
            return getPathByCondition(
                endInnerPoint.y < end.y,
                [start,
                    startInnerPoint,
                    { x: startInnerPoint.x, y: rightTop.y - minGap },
                    { x: endInnerPoint.x, y: rightTop.y - minGap },
                    end],
                [start,
                    startInnerPoint,
                    { x: startInnerPoint.x, y: rightBottom.y + minGap },
                    { x: endInnerPoint.x, y: rightBottom.y + minGap },
                    end],
            );
        }
        if (throughEnd) {
            const [leftTop, rightTop, rightBottom, leftBottom] = getFourPoints(end);
            if (end.x === endInnerPoint.x) {
                return getPathByCondition(
                    startInnerPoint.x < start.x,
                    [start,
                        { x: leftTop.x - minGap, y: startInnerPoint.y },
                        { x: leftTop.x - minGap, y: endInnerPoint.y },
                        endInnerPoint,
                        end],
                    [start,
                        { x: rightTop.x + minGap, y: startInnerPoint.y },
                        { x: rightTop.x + minGap, y: endInnerPoint.y },
                        endInnerPoint,
                        end],
                );
            }
            return getPathByCondition(
                startInnerPoint.y < start.y,
                [start,
                    { x: startInnerPoint.x, y: rightTop.y - minGap },
                    { x: endInnerPoint.x, y: rightTop.y - minGap },
                    endInnerPoint,
                    end],
                [start,
                    { x: startInnerPoint.x, y: rightBottom.y + minGap },
                    { x: endInnerPoint.x, y: rightBottom.y + minGap },
                    endInnerPoint,
                    end],
            );
        }
    }
    return points;
}

export function fixBPathToMiddle(
    startMinX,
    startMaxX,
    startMinY,
    startMaxY,
    endMinX,
    endMaxX,
    endMinY,
    endMaxY,
    startInnerPoint,
    endInnerPoint,
    points,
) {
    if (compareThreeCondition(startMinY, endMaxY, points[2].y)) {
        const fixValue = (startMinY + endMaxY) / 2;
        if (startInnerPoint.y === points[2].y) {
            points[1].y = fixValue;
        } else {
            points[3].y = fixValue;
        }
        points[2].y = fixValue;
    } else if (compareThreeCondition(endMinY, startMaxY, points[2].y)) {
        const fixValue = (endMinY + startMaxY) / 2;
        if (startInnerPoint.y === points[2].y) {
            points[1].y = fixValue;
        } else {
            points[3].y = fixValue;
        }
        points[2].y = fixValue;
    } else if (compareThreeCondition(startMinX, endMaxX, points[2].x)) {
        const fixValue = (startMinX + endMaxX) / 2;
        if (startInnerPoint.x === points[2].x) {
            points[1].x = fixValue;
        } else {
            points[3].x = fixValue;
        }
        points[2].x = fixValue;
    } else if (compareThreeCondition(endMinX, startMaxX, points[2].x)) {
        const fixValue = (endMinX + startMaxX) / 2;
        if (startInnerPoint.x === points[2].x) {
            points[1].x = fixValue;
        } else {
            points[3].x = fixValue;
        }
        points[2].x = fixValue;
    }
}

export function getBetterPath(start, startInnerPoint, middlePointCase1, middlePointCase2, endInnerPoint, end) {
    let points;
    const allPointArray1 = [start, startInnerPoint, middlePointCase1, endInnerPoint, end];
    const allPointArray2 = [start, startInnerPoint, middlePointCase2, endInnerPoint, end];
    // 判断两种连接方式是否经过起点/终点，优先选择不通过的那种方案
    // 如果都通过，则需要添加额外的点进行连接
    // 如果都不通过，则计算两种方式的弯折次数，选择弯折少的那种
    const isCase1Through = isPathThroughStartOrEnd([start, startInnerPoint, middlePointCase1, endInnerPoint, end]);
    const isCase2Through = isPathThroughStartOrEnd([start, startInnerPoint, middlePointCase2, endInnerPoint, end]);
    if (booleanAnd(isCase1Through, isCase2Through)) {
        // 两种中点生成方式都经过起点与终点的情况下，只有可能是起点与终点都有连接的形状，且起点与终点的方向同为垂直或同为水平
        const {
            newStartInnerPoint,
            extraPoint1,
            extraPoint2,
            newEndInnerPoint,
        } = generateExtraPathPoint(startInnerPoint, endInnerPoint, start, end);
        points = [start, newStartInnerPoint, extraPoint1, extraPoint2, newEndInnerPoint, end];
    } else if (booleanAnd(!isCase1Through, !isCase2Through)) {
        const isCase1ThroughShape = isPathThroughShape(allPointArray1).isThrough;
        const isCase2ThroughShape = isPathThroughShape(allPointArray2).isThrough;
        if (booleanAnd(!isCase1ThroughShape, !isCase2ThroughShape)) {
            // 计算两种连接方式需要弯折的次数，以选择数量少的那种
            const case1Num = getPathTurnNum(allPointArray1);
            const case2Num = getPathTurnNum(allPointArray2);
            points = getPathByCondition(case1Num < case2Num, allPointArray1, allPointArray2);
        } else if (!isCase1ThroughShape) {
            points = allPointArray1;
        } else {
            points = allPointArray2;
        }
    } else if (!isCase1Through) {
        points = allPointArray1;
    } else {
        points = allPointArray2;
    }
    if (booleanAnd(booleanAnd(start.rect, end.rect), points.length === 5)) {
        const startFourPoint = getFourPoints(start);
        const endFourPoint = getFourPoints(end);
        startFourPoint.sort((a, b) => a.x - b.x);
        endFourPoint.sort((a, b) => a.x - b.x);
        const startMinX = startFourPoint[0].x;
        const startMaxX = startFourPoint[3].x;
        const endMinX = endFourPoint[0].x;
        const endMaxX = endFourPoint[3].x;
        startFourPoint.sort((a, b) => a.y - b.y);
        endFourPoint.sort((a, b) => a.y - b.y);
        const startMinY = startFourPoint[0].y;
        const startMaxY = startFourPoint[3].y;
        const endMinY = endFourPoint[0].y;
        const endMaxY = endFourPoint[3].y;
        // 此处是为了将路径上的一条边优化到两个图形的中间，计算逻辑与另一处优化方案相同，可参考另一处的注释
        fixBPathToMiddle(
            startMinX,
            startMaxX,
            startMinY,
            startMaxY,
            endMinX,
            endMaxX,
            endMinY,
            endMaxY,
            startInnerPoint,
            endInnerPoint,
            points,
        );
    }
    return points;
}

/** 该算法整体思路如下:
 *  根据起点与终点所连接形状的位置关系，生成两个在其附近的点，称为内联点
 *  这样两个内联点的连接方式只有两种，分别为以两个内联点为对角线的矩形的左上两条边和右下两条边
 *  优先选择该路径上不经过起点与终点的那种连接方式
 *  如果都经过，则说明需要生成额外的点以对首尾两端连接的图形进行避让
 *  如果都不经过，则选择实际弯折次数较少的那种
 */
export function generateConnectorPoints(start, end) {
    let points;
    // 经过验证，start和end均需存在才可执行后续逻辑
    if (start && end) {
        const startInnerPoint = generateInnerPoint(start);
        const endInnerPoint = generateInnerPoint(end);
        const middlePointCase1 = {
            x: startInnerPoint.x,
            y: endInnerPoint.y,
        };
        const middlePointCase2 = {
            x: endInnerPoint.x,
            y: startInnerPoint.y,
        };
        points = getBetterPath(start, startInnerPoint, middlePointCase1, middlePointCase2, endInnerPoint, end);
        points = deleteExtraPoints(points);
        points = enhancedAvoidance(points);
    }
    return points;
}

function isNumber(val) {
    return typeof val === 'number' && !Number.isNaN(val);
}

function getLength(w, minSizeValue) {
    let width = Math.abs(w);

    if (!isNumber(width) || width < minSizeValue) {
        width = minSizeValue;
    }

    return width;
}

export function getConnectorStyleByPoints(realPoints: Point[]) {
    let points = realPoints;
    const minSizeValue = 4 / 3;
    const count = points.length;

    let p1 = points[0];
    let p2 = points[count - 1];
    const p3 = points[1];

    let x1 = p1.x;
    let y1 = p1.y;
    let x2 = p2.x;
    let y2 = p2.y;
    const x3 = p3.x;
    const y3 = p3.y;

    let w = x2 - x1;
    let h = y2 - y1;
    let rot = 0;
    let radians;
    const center = { x: (x1 + x2) / 2, y: (y1 + y2) / 2 };


    if (Math.round(x1) === Math.round(x3)) {
        rot = 90;
        radians = Math.PI / 2;
    } else if (Math.round(y1) !== Math.round(y3)) {
        radians = Math.atan2(y3 - y1, x3 - x1);
        rot = radians * 180 / Math.PI;
        if (radians < 0) {
            radians += 2 * Math.PI;
            rot += 360;
        }
    }

    if (rot) {
        // radians = -radians;
        points = points.map(pt => afterRotate(pt, 360 - rot, center.x, center.y));

        p1 = points[0];
        x1 = p1.x;
        y1 = p1.y;

        p2 = points[count - 1];
        x2 = p2.x;
        y2 = p2.y;
        w = x2 - x1;
        h = y2 - y1;
    }
    const flipH = w < 0;
    const flipV = h < 0;
    const scaleX = flipH ? -1 : 1;
    const scaleY = flipV ? -1 : 1;

    const width = getLength(w, minSizeValue);
    const height = getLength(h, minSizeValue);

    const adjs: number[] = [];
    for (let i = 0; i < count - 3; i++) {
        const point = points[i + 1];
        if (i % 2) {
            adjs.push((point.y - y1) / height * scaleY);
        } else {
            adjs.push((point.x - x1) / width * scaleX);
        }
    }

    const left = center.x - (width / 2);
    const top = center.y - (height / 2);

    return {
        rot,
        left,
        top,
        width,
        height,
        flipH,
        flipV,
        adjs,
    };
}
